import { RcSuspense } from '@/components/atoms/RcSuspense'
import { RcTrans } from '@/components/atoms/RcTrans'
import {
  RcIconButton,
  RcIconButtonProps
} from '@/components/molecules/interactive/RcIconButton'
import { RcTooltipButton } from '@/components/molecules/interactive/RcTooltipButton'
import { ShadowBox } from '@/components/molecules/interactive/ShadowBox'
import { useNotificationStyles } from '@/components/organisms/site/NotificationIcon'
import { useChat } from '@/components/providers/site/ChatProvider'
import { shiver } from '@/core/animations'
import { useIsTablet } from '@/core/hooks/useMediaQueries'
import { useNavigation } from '@/core/hooks/useNavigation'
import { RootRoute } from '@/core/route-keys'
import { useSocialUserContext } from '@/entity/chat/useSocialUserContext'
import Button from '@mui/material/Button'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import Stack from '@mui/material/Stack'
import { TooltipProps } from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { useCallback, useEffect, useState } from 'react'
import { ChatListContainer } from './ChatList'

export const ChatNotificationIcon = ({
  expanded,
  button,
  onSelected = () => {},
  ...props
}: Partial<RcIconButtonProps> & {
  expanded?: boolean
  button?: boolean
  onSelected?: () => void
}) => {
  const isMobile = useIsTablet()
  const { handleShowChat } = useChat()
  const { alerts: notifications } = useSocialUserContext()
  const { navTo } = useNavigation()

  const [open, setOpen] = useState(false)
  const style = useNotificationStyles()

  const unread = Object.keys(notifications || {}).length
  const hasNewMessages = !!unread

  useEffect(() => {
    setOpen(false)
  }, [expanded])

  const handleClick = async () => {
    setOpen(prev => !prev)
  }

  const handleClose = useCallback(async () => {
    setOpen(false)
  }, [])

  const tooltipProps: Omit<TooltipProps, 'children'> = {
    open: open,
    placement: isMobile ? 'bottom-start' : 'right-start',
    disableInteractive: false,
    componentsProps: {
      tooltip: {
        sx: style
      }
    },
    PopperProps: { keepMounted: true },
    title: open ? (
      <RcSuspense>
        <Stack height='100%' width='100%' alignContent='center'>
          <Stack
            direction='row'
            justifyContent='flex-start'
            spacing={2}
            sx={{ m: 4, width: 300 }}
          >
            <Typography variant='h3'>
              <RcTrans i18nKey='chat.messages' />
            </Typography>
            <Typography variant='h3' color='text.secondary'>
              {unread || ''}
            </Typography>
          </Stack>
          <ShadowBox>
            <ChatListContainer
              showChat={chat => {
                handleShowChat(chat)
                onSelected()
              }}
            />
          </ShadowBox>
          <Button
            variant='text'
            sx={{ m: 2 }}
            onClick={() => {
              navTo({
                root: RootRoute.Chat
              })
              onSelected()
            }}
          >
            <RcTrans i18nKey='chat.view-all-messages' />
          </Button>
        </Stack>
      </RcSuspense>
    ) : null
  }

  return (
    <ClickAwayListener touchEvent={false} onClickAway={handleClose}>
      {button ? (
        <RcTooltipButton
          onClick={() => {
            handleClick()
          }}
          size='large'
          icon={['fal', 'messages']}
          badge={hasNewMessages}
          TooltipProps={tooltipProps}
          disableTouchRipple
          variant='text'
          className={`rc-navigation-button ${open ? 'active' : ''}`}
          sx={shiver}
        >
          {expanded ? <RcTrans i18nKey='navigation.messages' /> : null}
        </RcTooltipButton>
      ) : (
        <RcIconButton
          onClick={() => {
            handleClick()
          }}
          size='small'
          IconProps={{ size: 'sm' }}
          icon={['fal', 'messages']}
          badge={hasNewMessages}
          TooltipProps={tooltipProps}
          disableTouchRipple
          sx={shiver}
          {...props}
        />
      )}
    </ClickAwayListener>
  )
}
